"use client";

import React from "react";
import { ArrowRight, BookOpen, CircleCheck as CheckCircle2, ShieldCheck, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
const services = [{
  title: "Training & Application Support",
  path: "/TrainingApplicationSupportPage",
  badge: "Method & Operator Training",
  description: "Structured operator instruction, analytical method development, and ongoing application guidance to maximize instrument uptime and measurement accuracy in regulated testing environments.",
  points: ["Method development and validation assistance", "Hands-on operator and administrator training", "Direct consultation with application specialists", "Custom standard operating procedure (SOP) guidance"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/training_support_lab.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/training_support_lab.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/training_support_lab.png-wvc-sizes",
  alt: "Laboratory specialist conducting instrument training and application support"
}, {
  title: "Qualification & Lifecycle Services",
  path: "/QualificationLifecycleServicesPage",
  badge: "Compliance & Maintenance",
  description: "Comprehensive compliance protocols, IQ/OQ/PQ validation, preventative maintenance, and rapid repair plans to keep your analytical instruments compliant throughout their lifecycle.",
  points: ["Installation and operational qualification (IQ/OQ/PQ)", "Periodic calibration and preventive maintenance", "Rapid breakdown diagnostics and genuine parts repair", "Regulatory audit readiness and lifecycle documentation"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/qualification_lifecycle_instruments.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/qualification_lifecycle_instruments.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/qualification_lifecycle_instruments.png-wvc-sizes",
  alt: "Analytical instrument undergoing qualification and lifecycle inspection"
}];
export default function RelatedServices() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="relatedservices" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-2xl mb-12 md:mb-16">
          <Badge variant="outline" className="mb-3 px-3 py-1 text-xs uppercase tracking-wider font-semibold border-primary/30 text-primary">
            Complete Laboratory Support
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4">
            Related Technical Services
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            Strengthen your laboratory operations with specialized application training, validation protocols, and lifecycle maintenance delivered across Ireland and the UK.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
          {services.map((service, index) => <motion.div key={service.path} data-index={index} initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-50px"
        }} transition={{
          duration: 0.5,
          delay: index * 0.15,
          ease: [0.25, 1, 0.5, 1]
        }} className="flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-300 rounded-xl overflow-hidden group">
                <div className="relative h-56 md:h-64 overflow-hidden bg-muted">
                  <img src={service.image} data-wvc-srcset={service.imageSrcset} data-wvc-sizes={service.imageSizes} alt={service.alt} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out" />
                  <div className="absolute top-4 left-4">
                    <Badge variant="secondary" className="shadow-xs backdrop-blur-md bg-background/90 text-foreground font-medium">
                      {service.badge}
                    </Badge>
                  </div>
                </div>

                <div className="flex-1 flex flex-col p-6 md:p-8">
                  <CardHeader className="p-0 mb-4 gap-2">
                    <CardTitle className="text-2xl font-bold text-card-foreground group-hover:text-primary transition-colors">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground text-base leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="p-0 flex-1 my-4">
                    <ul className="space-y-2.5">
                      {service.points.map((point, pointIndex) => <li key={pointIndex} data-index={pointIndex} className="flex items-start gap-2.5 text-sm text-card-foreground">
                          <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <span>{point}</span>
                        </li>)}
                    </ul>
                  </CardContent>

                  <CardFooter className="p-0 pt-6 mt-auto border-t border-border">
                    <Button asChild className="w-full sm:w-auto gap-2 group/btn" size="lg">
                      <Link to={service.path}>
                        <span>Explore {service.title}</span>
                        <ArrowRight className="size-4 group-hover/btn:translate-x-1 transition-transform" />
                      </Link>
                    </Button>
                  </CardFooter>
                </div>
              </Card>
            </motion.div>)}
        </div>
      </div>
    </section>;
}